import { combineReducers } from 'redux';
import { createSelector } from 'reselect';

import adjustedFlow from './adjustedFlowReducer';
import affordability from './memberAffordabilityReducer';
import cachedData from './cachedDataReducer';
import counties from './countiesReducer';
import countries from './countriesReducer';
import data, * as fromDataReducer from './dataReducer';
import dataSnapshot from './dataSnapshotReducer';
import direction from './directionReducer';
import disabledUi from './disabledUiReducer';
import enrollments, * as fromEnrollmentsReducer from './enrollmentsReducer';
import exception from './exceptionReducer';
import flow, * as fromFlowReducer from './flowReducer';
import form, * as fromFormReducer from './formReducer';
import hyperReview from './hyperReviewReducer';
import launchData, * as fromLaunchData from './launchReducer';
import linkFrom from './linkFromReducer';
import methods from './methodsReducer';
import page from './activePageReducer';
import previousData, * as fromPreviousDataReducer from './previousDataReducer';
import relations from './bloodRelationsReducer';
import scroll from './scrollReducer';
import settings, * as fromSettingsReducer from './settingsReducer';
import structure from './structureReducer';
import summary, * as fromSummaryReducer from './summaryReducer';
import tribes from './tribeReducer';

import { PAGES } from '@constants/ssap/flows/pagesTypes';
import { isEqual } from '@utils/miscellaneous/objectHelpers';

export default combineReducers({
    adjustedFlow,
    affordability,
    cachedData,
    counties,
    countries,
    data,
    dataSnapshot,
    direction,
    disabledUi,
    enrollments,
    exception,
    flow,
    form,
    hyperReview,
    launchData,
    linkFrom,
    methods,
    page,
    previousData,
    relations,
    scroll,
    settings,
    structure,
    summary,
    tribes
});

// Adjusted Flow selectors

export const selectAdjustedFlow = state => state.adjustedFlow;

// Data selectors

export const getCachedData = state => state.cachedData;
export const getApplicationId = state => fromDataReducer.getApplicationId(state.data);
export const getCaseNumber = state => fromDataReducer.getCaseNumber(state.data);
export const getCoverageYear = state => fromDataReducer.getCoverageYear(state.data);
export const getSsapData = state => state.data;
export const getLaunchData = state => state.launchData;
export const selectPrimaryTaxFiler = state => fromDataReducer.selectPrimaryTaxFiler(state.data);
export const selectBloodRelationships = state => fromDataReducer.selectBloodRelationships(state.data);
export const selectHouseholdMembers = state => fromDataReducer.selectHouseholdMembers(state.data);
export const selectMaxAchievedPage = state => fromDataReducer.selectMaxAchievedPage(state.data);
export const getApplicantGuids = state => fromDataReducer.getApplicantGuids(state.data);
export const selectIfRenewalFlow = state => fromDataReducer.selectIfRenewalFlow(state.data);
export const selectIfSsapOpenInReviewMode = state => fromDataReducer.selectIfSsapOpenInReviewMode(state.data);
export const selectCoverageIndicators = state => fromDataReducer.selectCoverageIndicators(state.data);
export const selectCurrentlyPregnantIndicators = state => fromDataReducer.selectCurrentlyPregnantIndicators(state.data);
export const selectAddressVisibilityDueToGeneralDelivery = state => fromDataReducer.selectAddressVisibilityDueToGeneralDelivery(state.data);
export const selectPrimaryContactPerson = state => fromDataReducer.selectPrimaryContactPerson(state.data);
export const selectPrimaryContactPersonHomeAddress = state => fromDataReducer.selectPrimaryContactPersonHomeAddress(state.data);


// Enrollments selectors

export const getApplicantGuidsFromEnrollments = state => fromEnrollmentsReducer.getApplicantGuidsFromEnrollments(state.enrollments);
export const selectBulkEnrollments = state => fromEnrollmentsReducer.selectBulkEnrollments(state.enrollments);
export const selectAtomizedEnrollments = state => fromEnrollmentsReducer.selectAtomizedEnrollments(state.enrollments);
export const selectIsDataFetched = state => fromEnrollmentsReducer.selectIsDataFetched(state.enrollments);


// Form selectors

export const isFormInInitialState = state => fromFormReducer.isFormInInitialState(state.form);
export const selectFormSnapshot = (state, formName) => fromFormReducer.selectFormSnapshot(state.form, formName);
export const selectFormData = (state, formName) => fromFormReducer.selectFormData(state.form, formName);

// Flow selectors

export const getSsapFlow = state => fromFlowReducer.getSsapFlow(state.flow);
export const getIfFlowLoaded = state => fromFlowReducer.getIfFlowLoaded(state.flow);

// Launch selectors

export const selectIfSsapInReviewMode = state => fromLaunchData.selectIfSsapInReviewMode(state.launchData);

// Page selectors

export const selectPage = state => state.page;


// Previous SSAP data selectors

export const selectIfPreviousDataLoaded = state => fromPreviousDataReducer.selectIfPreviousDataLoaded(state.previousData);
export const selectSsapPreviousData = state => state.previousData;
export const getPreviousApplicantGuids = state => fromPreviousDataReducer.getPreviousApplicantGuids(state.previousData);
export const getPreviousApplicationId = state => fromPreviousDataReducer.getPreviousApplicationId(state.previousData);

// Relations selectors

export const selectRelations = state => state.relations;

// Settings selectors

export const selectIfEscUIEnabled = state => fromSettingsReducer.selectIfEscUIEnabled(state.settings);
export const selectIfFlipLogicForLivesWithParentEnabled = state => fromSettingsReducer.selectIfFlipLogicForLivesWithParentEnabled(state.settings);
export const selectIfInlineEligibilityIgnoredForEscPage = state => fromSettingsReducer.selectIfInlineEligibilityIgnoredForEscPage(state.settings);
export const selectIfAdditionalResidencyEnabled = state => fromSettingsReducer.selectIfAdditionalResidencyEnabled(state.settings);
export const selectIfLivingArrangement = state => fromSettingsReducer.selectIfLivingArrangement(state.settings);
export const selectIsItfFeatureEnabled = state => fromSettingsReducer.selectIsItfFeatureEnabled(state.settings);
export const selectIsItuQuestionsDisabled = state => fromSettingsReducer.selectIsItuQuestionsDisabled(state.settings);
export const selectIsInlineCurrentIncomeEnabled = state => fromSettingsReducer.selectIsInlineCurrentIncomeEnabled(state.settings);
export const selectIsMemberRemovalReasonEnabled = state => fromSettingsReducer.selectIsMemberRemovalReasonEnabled(state.settings);
export const selectDacaEffective = state => fromSettingsReducer.selectDacaEffective(state.settings);
export const selectIfRealtimeCheckEnabled = state => fromSettingsReducer.selectIfRealtimeCheckEnabled(state.settings);
export const selectIfSogiFeatureEnabled = state => fromSettingsReducer.selectIfSogiFeatureEnabled(state.settings);
export const selectSettings = state => state.settings;
export const selectIfMilitaryActiveDutyReturnQuestionEnabled = state => fromSettingsReducer.selectIfMilitaryActiveDutyReturnQuestionEnabled(state.settings);
export const selectIfMilitaryFamilyMemberQuestionEnabled = state => fromSettingsReducer.selectIfMilitaryFamilyMemberQuestionEnabled(state.settings);
export const selectIfAutoReenrollSopaConsentEnabled = state => fromSettingsReducer.selectIfAutoReenrollSopaConsentEnabled(state.settings);
export const selectIfVoterRegistrationFeatureEnabled = state => fromSettingsReducer.selectIfVoterRegistrationFeatureEnabled(state.settings);
export const selectIfAlwaysDisplayMedicaidQuestionsEnabled = state => fromSettingsReducer.selectIfAlwaysDisplayMedicaidQuestionsEnabled(state.settings);
export const selectIfShowStateEmployeeHealthBenefitPlanEnabled = state => fromSettingsReducer.selectIfShowStateEmployeeHealthBenefitPlanEnabled(state.settings);
export const selectIfMedicaidTerminationFeatureEnabled = state => fromSettingsReducer.selectIfMedicaidTerminationFeatureEnabled(state.settings);
export const selectMedicaidEndDateOverride = state => fromSettingsReducer.selectMedicaidEndDateOverride(state.settings);
export const selectIfNoSsnReasonCollectionEnabled = state => fromSettingsReducer.selectIfNoSsnReasonCollectionEnabled(state.settings);
export const selectEndDateOfPregnancyOverride = state => fromSettingsReducer.selectEndDateOfPregnancyOverride(state.settings);
export const selectIfQhpAllowedForMedicare = state => fromSettingsReducer.selectIfQhpAllowedForMedicare(state.settings);
export const selectIfShowFullTimeStudentEnabled = state => fromSettingsReducer.selectIfShowFullTimeStudentEnabled(state.settings);
export const selectIfEnableAddressConfidentiality = state => fromSettingsReducer.selectIfEnableAddressConfidentiality(state.settings);


// Structure selectors

export const getStructure = state => state.structure;

// Summary selectors

export const selectIfOnlyUpdatedFieldsShown = state => fromSummaryReducer.selectIfOnlyUpdatedFieldsShown(state.summary);
export const selectIfExtraFinalSummaryShown = state => fromSummaryReducer.selectIfExtraFinalSummaryShown(state.summary);
export const selectIfSummaryCrashed = state => fromSummaryReducer.selectIfSummaryCrashed(state.summary);

// Combined selector

export const selectIfShouldInvokeInlineEligibility = createSelector(
    selectAdjustedFlow,
    selectPage,
    (flow, currentPageNumber) => {
        const lastExpectedIncomePageInFlow = flow
            .filter(({ pageName }) => pageName === PAGES.EXPECTED_INCOME_PAGE)
            .slice(-1)
            .pop();

        if(lastExpectedIncomePageInFlow) {
            const { pageIndexInFlow } = lastExpectedIncomePageInFlow;
            return pageIndexInFlow === currentPageNumber;
        }

        return false;
    }
);

export const selectIfFinalReviewPageActive = createSelector(
    selectAdjustedFlow,
    selectPage,
    (flow, currentPageNumber) => {
        if(!flow[currentPageNumber]) {
            return false;
        }
        const { pageName } = flow[currentPageNumber];
        return pageName === PAGES.FINAL_APPLICATION_REVIEW_PAGE;
    }
);

export const selectIfPageActiveByPageName = createSelector(
    (_, pageName) => pageName,
    selectAdjustedFlow,
    selectPage,
    (pageName, flow, currentPageNumber) => {
        if(!flow[currentPageNumber] || !pageName) {
            return false;
        }
        const { pageName: activePageName } = flow[currentPageNumber];
        return pageName === activePageName;
    }
);

export const selectIfJumpingRestricted = createSelector(
    selectAdjustedFlow,
    selectMaxAchievedPage,
    (flow, maxAchievedPage) => {
        return flow.length > maxAchievedPage + 1;
    }
);

export const selectIfShouldFetchEnrollments = createSelector(
    getApplicantGuids,
    getApplicantGuidsFromEnrollments,
    (applicantGuidsFromCurrentApplication, applicantGuidsFromEnrollments) => {
        return !isEqual(applicantGuidsFromCurrentApplication, applicantGuidsFromEnrollments);
    }
);

export const selectNonSeekingForCoverageAndEnrolled = createSelector(
    selectCoverageIndicators,
    selectHouseholdMembers,
    selectBulkEnrollments,
    (coverageIndicators, hhms, enrollments) => {
        return hhms.reduce((result, hhm, idx) => {
            const { applicantGuid } = hhm;

            const hasEnrollment = !!enrollments
                .find(enrollment => enrollment.memberGuid === applicantGuid && enrollment.hasActiveHealthEnrollment);

            return hasEnrollment && coverageIndicators[idx] === false
                ? [ ...result, applicantGuid ]
                : result;
        }, []);
    }
);

export const selectCurrentlyPregnantAndEnrolled = createSelector(
    selectCurrentlyPregnantIndicators,
    selectHouseholdMembers,
    selectBulkEnrollments,
    (currentPregnantIndicators, hhms, enrollments) => {
        return hhms.reduce((result, hhm, idx) => {
            const { applicantGuid } = hhm;

            const hasEnrollment = !!enrollments
                .find(enrollment => enrollment.memberGuid === applicantGuid && enrollment.hasActiveHealthEnrollment);

            return hasEnrollment && currentPregnantIndicators[idx] === true
                ? [ ...result, applicantGuid ]
                : result;
        }, []);
    }
);

export const selectFirstMatchedPage = createSelector(
    (_, pageName) => pageName,
    selectAdjustedFlow,
    (pageName, flow) => {
        const index = flow.findIndex(page => page.pageName === pageName);
        return index !== -1 ? index : null;
    }
);

export const selectIfMaxProgressAchieved = createSelector(
    getSsapData,
    selectAdjustedFlow,
    (data, flow) => data.maxAchievedPage === (flow.length - 1)
);

export const getActivePageName = createSelector(
    selectAdjustedFlow,
    selectPage,
    (flow, page) => flow[page]?.pageName
);